import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useId, useState } from "react";

import { queryKey } from "../../../api/endpoints";
import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { OnboardingModule, OnboardingPractice } from "../../../api/types";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { ConfirmDialog } from "../../../ui/ConfirmDialog";
import { Pill } from "../../../ui/Pill";
import { showToast } from "../../../ui/toastStore";
import { usePracticeSpace } from "./usePracticeSpace";

interface PracticePanelProps {
  practice: OnboardingPractice;
  modules: readonly OnboardingModule[];
}

/** "Module E3", or "A refresher on <topic>" when the tutor opened the space for a refresher on a topic. */
export function moduleWords(practice: OnboardingPractice): string {
  if (practice.module === "refresher") return practice.topic ? `A refresher on ${practice.topic}` : "A refresher";
  return `Module ${practice.module ?? ""}`;
}

/**
 * The practice space (architecture 7.4): a private copy where nothing is saved to the discipline or written to the
 * files. This panel says whether the person is in it, lets them go in and out, open a fresh copy for a module, and wipe it.
 */
export function PracticePanel({ practice, modules }: PracticePanelProps) {
  const queryClient = useQueryClient();
  const space = usePracticeSpace();
  const start = useApiCommand("start_practice");
  const wipe = useApiCommand("wipe_practice");
  const pickId = useId();
  const firstOpen = modules.find((module) => module.status === "started") ?? modules.find((module) => module.status === "not_started") ?? modules[0];
  const [picked, setPicked] = useState<string>("");
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const chosen = picked || firstOpen?.module || "";
  const busy = space.busy || start.isPending || wipe.isPending;

  const keepOnboarding = (view: unknown) => queryClient.setQueryData(queryKey("onboarding", {}) as unknown as QueryKey, view);

  const open = async () => {
    if (!chosen) return;
    setProblem(null);
    try {
      keepOnboarding((await start.mutateAsync({ module: chosen })).data);
      await space.enter();
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  const doWipe = async () => {
    setProblem(null);
    try {
      keepOnboarding((await wipe.mutateAsync({})).data);
      setConfirmWipe(false);
      showToast({ text: "Your practice space is wiped." });
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <Card className="ra-section ra-practice" aria-labelledby="ra-practice-title">
      <h2 id="ra-practice-title">Practice space</h2>
      <p className="muted">
        A private copy, only yours. What you do there is never saved to the discipline and never written to the files. It stays between sittings so you can carry on, and you can wipe it at any time.
      </p>

      <p role="status" className="row">
        {space.inPractice ? (
          <>
            <Pill dashed>You are in the practice space</Pill>
            {practice.module && <span className="muted">{moduleWords(practice)}</span>}
          </>
        ) : practice.exists ? (
          <>
            <Pill>Your practice space is ready</Pill>
            {practice.module && <span className="muted">{moduleWords(practice)}</span>}
          </>
        ) : (
          <Pill>No practice space yet</Pill>
        )}
      </p>

      <div className="row">
        {space.inPractice ? (
          <Button variant="primary" onClick={() => void space.leave()} busy={space.busy}>
            Back to {space.disciplineName || "the discipline"}
          </Button>
        ) : practice.exists ? (
          <Button variant="primary" onClick={() => void space.enter()} busy={space.busy}>
            Go to the practice space
          </Button>
        ) : null}
        {practice.exists && (
          <Button onClick={() => setConfirmWipe(true)} disabled={busy}>
            Wipe my practice space
          </Button>
        )}
      </div>

      {modules.some((module) => module.module.startsWith("S")) && (practice.practice_cards ?? 0) === 0 && (
        <p className="muted ra-practice__no-cards">
          No practice cards yet: the practice material has none, so the SME modules find an empty inbox in the practice space. The modules can still be read and
          talked through with the tutor.
        </p>
      )}

      {!space.inPractice && modules.length > 0 && (
        <div className="row ra-practice__open">
          <label htmlFor={pickId} className="field__label">
            Open a fresh copy for
          </label>
          <select id={pickId} className="field__input" value={chosen} onChange={(event) => setPicked(event.target.value)} disabled={busy}>
            {modules.map((module) => (
              <option key={module.module} value={module.module}>
                {module.module}: {module.title}
              </option>
            ))}
          </select>
          <Button onClick={() => void open()} busy={start.isPending} disabled={busy || !chosen}>
            Open the practice space
          </Button>
        </div>
      )}

      {(problem ?? space.problem) && (
        <p role="alert" className="form-error">
          {problem ?? space.problem}
        </p>
      )}

      <ConfirmDialog
        open={confirmWipe}
        title="Wipe your practice space?"
        message="Everything you did there is removed. Your memory and the discipline itself stay as they are. You can open a fresh copy at any time."
        confirmLabel="Wipe it"
        cancelLabel="Keep it"
        busy={wipe.isPending}
        error={problem}
        onConfirm={() => void doWipe()}
        onCancel={() => setConfirmWipe(false)}
      />
    </Card>
  );
}
